import React, { useState, useRef } from "react";
import "@/App.css";
import { ReactLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  Hero, Ribbon, About, Services, Calculator, WhyUs, Gallery, Contact, Footer, Floating,
} from "@/sections";
import { T, PHONES, LOGO } from "@/data";

const Navbar = ({ t, lang, setLang, scrollTo }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "calculator", label: t.nav.calc },
    { id: "contact", label: t.nav.contact },
  ];
  const go = (id) => { scrollTo(id); setOpen(false); };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b border-mist" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} data-testid="nav-logo" className="flex items-center">
          <img src={LOGO} alt="P&P Construction" className="h-11 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id} data-testid={`nav-${l.id}`} onClick={() => go(l.id)}
              className="link-underline font-body font-medium text-sm uppercase tracking-wider text-navy"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center border border-mist" data-testid="lang-switcher">
            {["bg", "en"].map((l) => (
              <button
                key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                className={`px-3 py-1.5 text-xs font-body font-bold uppercase transition-colors ${lang === l ? "bg-navy text-white" : "text-steel hover:text-navy"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <a href={`tel:${PHONES[0].tel}`} data-testid="nav-call-btn" className="hidden sm:block">
            <Button className="rounded-none bg-copper hover:bg-copper-hover text-white font-body font-semibold uppercase tracking-wider text-xs h-10 px-5 transition-colors">
              <Phone className="h-4 w-4 mr-2" /> {t.nav.call}
            </Button>
          </a>
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="lg:hidden text-navy">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-mist"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} data-testid={`mobile-nav-${l.id}`}
                  className="py-3 text-left font-body font-medium uppercase tracking-wider text-navy border-b border-mist last:border-0">
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

function App() {
  const [lang, setLang] = useState("bg");
  const t = T[lang];
  const lenisRef = useRef(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const lenis = lenisRef.current?.lenis;
    if (lenis) lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <ReactLenis root ref={lenisRef}>
      <div className="App bg-white overflow-x-hidden">
        <Navbar t={t} lang={lang} setLang={setLang} scrollTo={scrollTo} />
        <main>
          <Hero t={t} scrollTo={scrollTo} />
          <Ribbon t={t} />
          <About t={t} />
          <Services t={t} />
          <Calculator t={t} scrollTo={scrollTo} />
          <WhyUs t={t} />
          <Gallery t={t} />
          <Contact t={t} />
        </main>
        <Footer t={t} />
        <Floating t={t} />
        <Toaster position="top-center" richColors />
      </div>
    </ReactLenis>
  );
}

export default App;
